/* [Modified by Codex at 2026-10-27] 为桌面图片提供完整显示与铺满宽度两档，保留手机和其他媒体原有行为 */
if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.desktopImageQuery = window.matchMedia('(min-width: 750px)');
        this.onImageClick = this.onImageClick.bind(this);
        this.onImageKeyDown = this.onImageKeyDown.bind(this);
        this.onImageBreakpointChange = this.onImageBreakpointChange.bind(this);
        this.onMediaContentChange = this.onMediaContentChange.bind(this);
      }

      connectedCallback() {
        super.connectedCallback();
        if (this.imageListenersAttached) return;
        this.imageListenersAttached = true;
        this.addEventListener('click', this.onImageClick);
        this.addEventListener('keydown', this.onImageKeyDown);
        this.desktopImageQuery.addEventListener('change', this.onImageBreakpointChange);
        this.mediaContent = this.querySelector('[role="document"]');
        this.mediaContentObserver = new MutationObserver(this.onMediaContentChange);
        if (this.mediaContent) this.mediaContentObserver.observe(this.mediaContent, { childList: true });
        if (this.hasAttribute('open')) this.showActiveMedia();
      }

      disconnectedCallback() {
        if (this.hasAttribute('open')) this.hide();
        else this.resetDesktopImageMode();
        this.removeEventListener('click', this.onImageClick);
        this.removeEventListener('keydown', this.onImageKeyDown);
        this.desktopImageQuery.removeEventListener('change', this.onImageBreakpointChange);
        this.mediaContentObserver?.disconnect();
        this.mediaContentObserver = null;
        this.mediaContent = null;
        this.activeMedia = null;
        this.imageListenersAttached = false;
      }

      hide() {
        if (this.isClosing) return;
        this.isClosing = true;
        try {
          super.hide();
        } finally {
          this.resetDesktopImageMode({ resetScroll: true });
          this.activeMedia = null;
          this.isClosing = false;
        }
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
      }

      showActiveMedia({ refreshFocus = false } = {}) {
        const hadDesktopImageMode = !!this.desktopImageState;
        this.resetDesktopImageMode();
        const container = this.querySelector('[role="document"]');
        const mediaId = this.openedBy?.getAttribute('data-media-id');
        const activeMedia =
          (mediaId && container?.querySelector(`[data-media-id="${mediaId}"]`)) ||
          container?.querySelector('[data-media-id]');
        this.activeMedia = activeMedia || null;
        if (!activeMedia) {
          if (hadDesktopImageMode || refreshFocus) this.refreshFocusTrap();
          return;
        }
        container.querySelectorAll(
          `[data-media-id]:not([data-media-id="${activeMedia.dataset.mediaId}"])`
        ).forEach((element) => {
          element.classList.remove('active');
        });
        activeMedia.classList.add('active');

        if (this.desktopImageQuery.matches && activeMedia.nodeName === 'IMG') {
          this.enableDesktopImageMode(activeMedia);
          this.refreshFocusTrap();
          return;
        }

        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.scrollIntoView();
        container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();

        if (hadDesktopImageMode || refreshFocus) this.refreshFocusTrap();
      }

      enableDesktopImageMode(image) {
        const attributes = {};
        ['sizes', 'role', 'tabindex', 'aria-label'].forEach((name) => {
          attributes[name] = image.getAttribute(name);
        });
        this.desktopImageState = { image, attributes };
        this.classList.add('product-media-modal--desktop-image');
        this.classList.remove('product-media-modal--zoomed');
        image.sizes = '100vw';
        image.setAttribute('role', 'button');
        image.setAttribute('tabindex', '0');
        this.updateImageActionLabel();
        this.resetMediaScroll();
      }

      resetDesktopImageMode({ resetScroll = false } = {}) {
        const state = this.desktopImageState;
        if (state) {
          Object.entries(state.attributes).forEach(([name, value]) => {
            if (value === null) state.image.removeAttribute(name);
            else state.image.setAttribute(name, value);
          });
        }
        this.desktopImageState = null;
        this.classList.remove('product-media-modal--desktop-image', 'product-media-modal--zoomed');
        if (resetScroll) this.resetMediaScroll();
      }

      resetMediaScroll() {
        const container = this.querySelector('[role="document"]');
        if (!container) return;
        container.scrollTop = 0;
        container.scrollLeft = 0;
      }

      isDesktopImageMode() {
        return this.hasAttribute('open') && this.desktopImageQuery.matches &&
          this.activeMedia?.nodeName === 'IMG' && this.classList.contains('product-media-modal--desktop-image');
      }

      updateImageActionLabel() {
        const state = this.desktopImageState;
        if (!state) return;
        const action = this.classList.contains('product-media-modal--zoomed')
          ? this.dataset.zoomOutLabel
          : this.dataset.zoomInLabel;
        const label = [state.attributes['aria-label'] || state.image.alt, action].filter(Boolean).join('. ');
        if (label) state.image.setAttribute('aria-label', label);
        else state.image.removeAttribute('aria-label');
      }

      toggleImageZoom() {
        if (!this.isDesktopImageMode()) return;
        this.classList.toggle('product-media-modal--zoomed');
        this.updateImageActionLabel();
        this.resetMediaScroll();
      }

      onImageClick(event) {
        if (event.target === this.activeMedia) this.toggleImageZoom();
      }

      onImageKeyDown(event) {
        if (!this.isDesktopImageMode() || event.target !== this.activeMedia) return;
        if (event.key !== 'Enter' && event.key !== ' ' && event.code !== 'Space') return;
        event.preventDefault();
        if (!event.repeat) this.toggleImageZoom();
      }

      onMediaPointerUp(event) {
        if (this.isDesktopImageMode()) return;
        super.onMediaPointerUp(event);
      }

      onImageBreakpointChange() {
        if (this.hasAttribute('open') && this.activeMedia?.nodeName === 'IMG') this.showActiveMedia();
      }

      onMediaContentChange() {
        if (!this.hasAttribute('open') || !this.activeMedia || this.mediaContent?.contains(this.activeMedia)) return;
        this.showActiveMedia({ refreshFocus: true });
      }

      refreshFocusTrap() {
        if (!this.hasAttribute('open')) return;
        const dialog = this.querySelector('[role="dialog"]');
        if (!dialog) return;
        if (this.isDesktopImageMode()) {
          const visibleControls = getFocusableElements(this).filter((element) => element.getClientRects().length > 0);
          trapFocus(this, dialog, visibleControls);
        } else {
          trapFocus(this, dialog);
        }
      }
    }
  );
}
/* [End] */
